import { useEffect, useRef, useState } from "react";

const ANIMATION_DURATION = 1200;
const ITEM_DELAY = 150;

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function isReducedMotion(): boolean {
  return (
    typeof window === "undefined" ||
    typeof IntersectionObserver === "undefined" ||
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}

function computeFrame(
  targets: number[],
  elapsed: number,
): { nextCounts: number[]; nextCompleted: boolean[]; allCompleted: boolean } {
  let allCompleted = true;
  const nextCounts: number[] = [];
  const nextCompleted: boolean[] = [];

  targets.forEach((target, index) => {
    const delay = index * ITEM_DELAY;
    if (elapsed < delay) {
      nextCounts.push(0);
      nextCompleted.push(false);
      allCompleted = false;
    } else {
      const progress = Math.min((elapsed - delay) / ANIMATION_DURATION, 1);
      nextCounts.push(progress < 1 ? Math.floor(easeOut(progress) * target) : target);
      nextCompleted.push(progress === 1);
      if (progress < 1) allCompleted = false;
    }
  });

  return { nextCounts, nextCompleted, allCompleted };
}

export function useCountUpAnimation(targets: number[]) {
  const [completed, setCompleted] = useState<boolean[]>(targets.map(() => true));
  const [displayCounts, setDisplayCounts] = useState<number[]>(targets);

  const containerRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isReducedMotion() || hasAnimated.current) return undefined;

    setDisplayCounts(targets.map(() => 0));
    setCompleted(targets.map(() => false));

    let animationFrameId = 0;
    let startTime: number | null = null;

    const tick = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const { nextCounts, nextCompleted, allCompleted } = computeFrame(targets, timestamp - startTime);
      setDisplayCounts(nextCounts);
      setCompleted(nextCompleted);
      if (!allCompleted) animationFrameId = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        hasAnimated.current = true;
        observer.disconnect();
        animationFrameId = requestAnimationFrame(tick);
      },
      { threshold: 0.5 },
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      if (animationFrameId !== 0) cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return { containerRef, displayCounts, completed };
}
